import { FormRoot } from "@/components/FormRoot";
import { Input } from "@/components/Input";
import { CheckBox } from "@/components/CheckBox";
import { Button } from "@/components/Button";
import { FormSubmit } from "@/components/FormSubmit";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { publicApi } from "@/services/publicApi";
// DISABLED (multi-account):
// import { getAddAccountConnectTargetUserId, isAddAccountMode } from "@/utils/auth";
import { AxiosError } from "axios";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import logger from "@/utils/logger";
import { useToast } from "@/hooks/useToast";
import { INVALID_ORG_CODE, SIGNUP_FAILED } from "@/utils/toastMessages";
import { useState, useEffect } from "react";
import {
  termsAndConditionsToggled,
  signupLinkedInClicked,
  signupFormSubmitted,
  termsOfServiceClicked,
  privacyPolicyClicked,
} from "@/utils/tracking";
import { LinkedInBrandIcon, ArrowRightIcon } from "@/components/icons/icons";
import { buildLinkedInAuthUrl } from "@/utils/linkedin";

const userSignUpSchema = z.object({
  firstName: z.string().trim().min(1, { message: "Please enter your first name" }),
  lastName: z.string().trim().min(1, { message: "Please enter your last name" }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email address" })
    .transform((val) => val.toLowerCase()),
  accepted: z
    .boolean()
    .refine((val) => val, { message: "Please accept the Terms & Conditions to proceed" }),
  organizationCode: z.string().optional(),
});

type UserSignUpFormData = {
  firstName: string;
  lastName: string;
  email: string;
  accepted: boolean;
  organizationCode?: string;
};

interface TermsCheckboxProps {
  accepted: boolean;
  onToggle: (isAccepted: boolean) => void;
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
}

const TermsCheckbox = ({ accepted, onToggle, onOpenTerms, onOpenPrivacy }: TermsCheckboxProps) => (
  <div className="bg-theme-terms-box-bg border border-theme-terms-box-border rounded-[14px] px-4 h-[58px] flex items-center">
    <CheckBox id="accepted" checked={accepted} onChange={(val) => onToggle(Boolean(val))}>
      I agree to the{" "}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenTerms();
        }}
        className="text-primary-purple font-semibold"
      >
        Terms of Service
      </button>{" "}
      and{" "}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpenPrivacy();
        }}
        className="text-primary-purple font-semibold"
      >
        Privacy Policy
      </button>
    </CheckBox>
  </div>
);

interface UserSignUpFormContentProps {
  role: "achiever" | "advisor";
  defaultCode?: string;
  joinProgramId?: string;
}

/**
 * Step 1 ("Create Your Account") — shared by both achiever and Expert (advisor)
 * signup. Both roles collect the exact same account fields (name, email,
 * optional org code, terms); only the submitted `role` and tracking labels
 * differ.
 */
export const UserSignUpFormContent = ({
  role,
  defaultCode,
  joinProgramId,
}: UserSignUpFormContentProps) => {
  const [searchParams] = useSearchParams();
  const codeParam = defaultCode || searchParams.get("code") || "";
  const prefillEmail = searchParams.get("email") || "";
  // The referrer's user id from a referral link (?ref=). Sent with both the
  // email and LinkedIn signups -- it is what attributes the new account to the
  // person who referred them; an unknown value is ignored server-side.
  const refParam = searchParams.get("ref") || undefined;
  const isProgramJoin = !!joinProgramId;
  // Signup starts with the Terms box ticked, for Experts and Achievers alike.
  // It can still be unticked, which blocks both continue buttons as before.
  const termsCheckedByDefault = true;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<UserSignUpFormData>({
    resolver: zodResolver(userSignUpSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: prefillEmail,
      accepted: termsCheckedByDefault,
      organizationCode: codeParam,
    },
  });

  const { showToast } = useToast();
  const [accepted, setAccepted] = useState(termsCheckedByDefault);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const openLegalPageInNewTab = (path: "/legal/terms" | "/legal/privacy") => {
    if (path === "/legal/terms") {
      termsOfServiceClicked({ page: "signup" });
    } else {
      privacyPolicyClicked({ page: "signup" });
    }
    const legalUrl = `${window.location.origin}${path}`;
    window.open(legalUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    if (codeParam) setValue("organizationCode", codeParam);
    if (prefillEmail) setValue("email", prefillEmail);
  }, [codeParam, prefillEmail, setValue]);

  const onSubmit = async (data: UserSignUpFormData) => {
    setLoading(true);
    signupFormSubmitted({ role, has_org_code: Boolean(data.organizationCode) });
    try {
      // DISABLED (multi-account): add-account mode supplied connectToUserId here
      // so the backend linked the new signup to the opener's account.
      const response = await publicApi.auth.signup({
        email: data.email.toLowerCase(),
        referredByUserId: refParam,
        fullName: `${data.firstName} ${data.lastName}`.trim(),
        role,
        mobileNumber: "",
        // Group/Company code is achiever-specific group pricing — advisors don't send it.
        organizationCode: role === "achiever" ? data.organizationCode || "" : "",
        ...(joinProgramId && joinProgramId !== "New"
          ? {
              expertProgramExternalId: joinProgramId,
              expertProgramUrlRole: (localStorage.getItem("program_join_role") || "achiever") as
                | "achiever"
                | "advisor",
            }
          : joinProgramId === "New"
            ? { createExpertProgram: true }
            : {}),
      });

      localStorage.setItem("signup_timestamp", Date.now().toString());
      localStorage.setItem(
        "signup_tracking_data",
        JSON.stringify({
          user_role: role,
          signup_code: role === "achiever" ? data.organizationCode || null : null,
          utm_source: searchParams.get("utm_source"),
          utm_medium: searchParams.get("utm_medium"),
          utm_campaign: searchParams.get("utm_campaign"),
        }),
      );

      navigate("/email-sent", {
        state: {
          email: data.email,
          role,
          sessionToken: response.sessionToken,
        },
      });
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response?.status === 400) {
        logger.error(err.response.data);
        const detail = err.response.data?.detail || SIGNUP_FAILED;
        // An unknown Group / Company Code is a 400 from the signup endpoint --
        // show the same friendly message step 2 uses instead of the raw detail.
        const isInvalidOrgCode =
          typeof detail === "string" && detail.toLowerCase().includes("organization code");
        showToast({
          type: "error",
          message: isInvalidOrgCode ? INVALID_ORG_CODE : detail,
          duration: 3000,
        });
        if (detail.includes("already exists")) {
          navigate("/login", {
            state: { email: data.email, role, joinProgramId },
          });
        }
      } else {
        showToast({ type: "error", message: SIGNUP_FAILED, duration: 3000 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Heading */}
      <div className="flex flex-col gap-3">
        <h1
          className="text-36 font-bold leading-tight tracking-[0.37px]"
          style={{
            background: "var(--auth-heading-gradient)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          Create Your Account
        </h1>
        <p className="text-18 text-theme-text-secondary leading-7 tracking-[-0.44px]">
          {joinProgramId
            ? "Create your account to join your expert program"
            : role === "advisor"
              ? "Share your expertise as an Expert through Programs, Personal Advisory Boards and co-Expert engagements."
              : "Explore Expert Programs, build a Personal Advisory Board and take structured action toward your next career goal."}
        </p>
      </div>

      {/* LinkedIn */}
      <Button
        variant="linkedin"
        fullWidth
        size="lg"
        className="h-14 text-16 font-semibold"
        leftIcon={<LinkedInBrandIcon />}
        onClick={() => {
          signupLinkedInClicked({ role, has_org_code: Boolean(codeParam) });
          // DISABLED (multi-account): connectToUserId (4th arg) came from
          // add-account mode and linked the two accounts after LinkedIn auth.
          window.location.href = buildLinkedInAuthUrl(
            role,
            codeParam,
            "signup",
            undefined,
            joinProgramId !== "New" ? joinProgramId : undefined,
            joinProgramId === "New" ? true : undefined,
            refParam,
          );
        }}
        type="button"
        disabled={!accepted}
      >
        Continue with LinkedIn
      </Button>

      <FormRoot size="full" className="flex flex-col gap-6" onSubmitHandler={handleSubmit(onSubmit)}>
        {/* Divider */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-theme-border" />
          <span className="text-13 text-theme-text-secondary font-medium whitespace-nowrap">
            or sign up with email
          </span>
          <div className="flex-1 h-px bg-theme-border" />
        </div>

        {/* First Name + Last Name */}
        <div className="grid grid-cols-2 gap-6">
          <Input
            id="firstName"
            label="First Name"
            required
            placeholder="Alex"
            error={errors.firstName?.message}
            {...register("firstName")}
          />
          <Input
            id="lastName"
            label="Last Name"
            required
            placeholder="Mollen"
            error={errors.lastName?.message}
            {...register("lastName")}
          />
        </div>

        {/* Email */}
        <Input
          id="email"
          label="Email Address"
          required
          type="email"
          placeholder="alex@company.com"
          readOnly={!!prefillEmail}
          error={errors.email?.message}
          {...register("email")}
        />

        {/* Group / Company Code — optional, achievers only: it is group
            pricing, and Expert signup never sends it (see onSubmit). */}
        {role === "achiever" && (
          <Input
            id="organizationCode"
            label="Group / Company Code"
            optional
            placeholder="e.g., ACME-2026"
            hint="If your company has a Forward plan, enter your code for group pricing."
            error={errors.organizationCode?.message}
            readOnly={isProgramJoin}
            {...register("organizationCode")}
          />
        )}

        {/* Terms */}
        <TermsCheckbox
          accepted={accepted}
          onToggle={(isAccepted) => {
            setAccepted(isAccepted);
            setValue("accepted", isAccepted);
            trigger("accepted");
            termsAndConditionsToggled({ accepted: isAccepted, page: "signup" });
          }}
          onOpenTerms={() => openLegalPageInNewTab("/legal/terms")}
          onOpenPrivacy={() => openLegalPageInNewTab("/legal/privacy")}
        />
        {errors.accepted && <p className="text-12 text-error-500 -mt-4">{errors.accepted.message}</p>}

        {/* Submit */}
        <FormSubmit
          className="h-12 md:h-14 text-14 md:text-16 font-semibold rounded-[14px] bg-progress-gradient"
          isLoading={loading}
          disabled={loading || !accepted}
          rightIcon={<ArrowRightIcon />}
        >
          Continue to Profile
        </FormSubmit>
      </FormRoot>
    </div>
  );
};
